import React, { ReactElement, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { OL_COLOUR_NAVY_BABY } from '../../../components/Style/colours';
import { Tooltip } from '../../../Tooltip/Tooltip';
// import { Link } from '../../Link';
// import { ROUTE_CONTACT_PAGE } from '../../../../ReduxStorage/routeActions';
import {
  FormWrapper,
  StyledTextField,
  TextFieldLabel,
  SubHeading,
  Heading,
  InstitutionsTablePaginationContainer,
  InsitutionsSectionWrapper,
  InstitutionUrlFieldWrapper,
  OLUrlPrefix,
  SelectFlexContainer,
  SelectTotalPagesContainer,
  StyledActionButton,
  StyledLink,
} from './styles';
import { validatePath } from '../../CreateInstitutionPage/helpers';
import {
  canPlanCreateAnInstitituion,
  convertInstitutionNameToUrlPath,
} from '../helpers';
import { AlertMessage } from '../../../components/AlertMessage/component';
import { InstitutionSummary } from '../../../../ReduxStorage/Institutions/types';
import { InstitutionsAdminTable } from '../../../components/InstitutionsAdminTable/component';
import { Paginate } from '../../../components/Pagination/Paginate';
import { Select } from '../../../components/Select/component';
import { TextField } from '../../../components/TextField/component';
import { UserSummary } from '../../../../ReduxStorage/Users/constants';
import { SubscriptionPlanType } from '../../../../ReduxStorage/SubscriptionPlans/constants';
import { alertDialog as alert } from '../../../components/Modals/AlertDialog';

interface ManageInstitutionProps {
  portalLimit: number | null;
  planType: SubscriptionPlanType;
  currentUserProfileName: string;
  institutions: InstitutionSummary[];
  owners: UserSummary[];
  createInstitutionPortal: (
    institutionName: string,
    institutionPath: string
  ) => Promise<void>;
  addAdmin: (institutionId: string) => Promise<void>;
}

const DEFAULT_INSTITUTIONS_PER_PAGE = 5;

export const ManageInstitution = React.memo(
  ({
    portalLimit,
    planType,
    currentUserProfileName,
    institutions,
    owners,
    createInstitutionPortal,
    addAdmin,
  }: ManageInstitutionProps): ReactElement => {
    const [institutionName, setInsitutionName] = useState<string>('');
    const [institutionURL, setInstitutionURL] = useState<string>('');
    const [validURL, setValidURL] = useState<boolean>(false);
    const [canAutocompleteURL, setCanAutocompleteURL] = useState<boolean>(true);
    const [institutionsTablePage, setInstitutionsTablePage] = useState<number>(
      1
    );
    const [institutionsPerPage, setInsitutionsPerPage] = useState<number>(
      DEFAULT_INSTITUTIONS_PER_PAGE
    );
    const [
      institutionSearchValue,
      setInstitutionSearchValue,
    ] = useState<string>('');
    const [filteredInstitutions, setFilteredInstitutions] = useState<
      InstitutionSummary[]
    >(institutions);

    const onChangeInstitutionName = (value: string): void => {
      setInsitutionName(value);

      if (canAutocompleteURL) {
        setInstitutionURL(convertInstitutionNameToUrlPath(value));
      }
    };

    const onChangeInstitutionURL = (value: string): void => {
      const lowerCaseURL = value.toLocaleLowerCase();
      setInstitutionURL(lowerCaseURL);
    };

    const handleNameFieldOnFocus = (): void => {
      const canAutocomplete = institutionURL === '';
      setCanAutocompleteURL(canAutocomplete);
    };

    const onCreateInstitution = async (): Promise<void> => {
      let errorMessage = null;
      if (!institutionName) {
        errorMessage = 'Institution name is required.';
      } else if (!institutionURL) {
        errorMessage = 'Institution URL is required';
      } else if (!validURL) {
        errorMessage = 'This URL has been taken or cannot be used';
      }
      if (errorMessage) {
        alert({
          title: 'Error',
          message: errorMessage,
        });
        throw Error();
      }
      await createInstitutionPortal(institutionName, institutionURL);
    };

    useMemo(() => {
      validatePath(institutionURL, setValidURL);
    }, [institutionURL]);

    useMemo(() => {
      setFilteredInstitutions(
        institutions.filter((institution) =>
          institution.name
            .toLocaleLowerCase()
            .includes(institutionSearchValue.toLocaleLowerCase())
        )
      );
    }, [institutionSearchValue, institutions]);

    const canCreateInstitution = canPlanCreateAnInstitituion(
      portalLimit,
      planType,
      institutions
    );

    const institutionsTableStart =
      (institutionsTablePage - 1) * institutionsPerPage;
    const institutionsTableEnd = institutionsTableStart + institutionsPerPage;

    return (
      <div>
        <Heading>Manage Institution</Heading>
        {canCreateInstitution && (
          <AlertMessage
            kind="info"
            title="Institution URL cannot be changed"
            message={
              "Please note that once you have chosen your institution URL and click on 'Create institution', it is permanent and cannot be changed."
            }
          />
        )}
        {!canCreateInstitution && (
          <AlertMessage
            kind="info"
            title="Subscription plan limited to one institution"
            message={
              <span>
                Your current subscription plan does not allow for multiple
                institutions to be created. Please{' '}
                <a href="https://www.openlearning.com/contact/">contact us</a>{' '}
                if you would like to discuss upgrading your current
                subscription.
              </span>
            }
          />
        )}
        <SubHeading>Create an institution</SubHeading>
        <FormWrapper>
          <TextFieldLabel>Institution name*</TextFieldLabel>
          <StyledTextField
            disabled={!canCreateInstitution}
            placeholder="E.g. OpenLearning"
            value={institutionName}
            onValueChange={onChangeInstitutionName}
            onFocus={handleNameFieldOnFocus}
          />
          <Tooltip
            placement="right"
            trigger="click"
            overlay={
              <span>
                If you have a custom domain please contact us (
                <StyledLink href="https://www.openlearning.com/contact/">
                  https://www.openlearning.com/contact/
                </StyledLink>
                {/* @TODO: Change the a tag with Link component after */}
                {/* <Link to={{ type: ROUTE_CONTACT_PAGE }}>
                  https://www.openlearning.com/contact/
                </Link> */}
                )
              </span>
            }
          >
            <TextFieldLabel hoverable>
              Institution URL*{'  '}
              <FontAwesomeIcon
                icon={faInfoCircle}
                color={OL_COLOUR_NAVY_BABY}
              />
            </TextFieldLabel>
          </Tooltip>
          <InstitutionUrlFieldWrapper>
            <OLUrlPrefix>Openlearning.com/</OLUrlPrefix>
            <StyledTextField
              disabled={!canCreateInstitution}
              value={institutionURL}
              onValueChange={onChangeInstitutionURL}
            />
          </InstitutionUrlFieldWrapper>
          <StyledActionButton
            label="Create institution"
            colour="cta-pink"
            disabled={!canCreateInstitution}
            onClick={() => onCreateInstitution()}
          />
        </FormWrapper>
        <InsitutionsSectionWrapper>
          <SubHeading>Join an existing institution</SubHeading>
          <TextField
            label="Search by institution"
            className="textfield"
            value={institutionSearchValue}
            onValueChange={(value) => {
              setInstitutionSearchValue(value);
            }}
            placeholder="Search here..."
            icon="search"
          />
          <InstitutionsAdminTable
            profileName={currentUserProfileName}
            institutions={filteredInstitutions.slice(
              institutionsTableStart,
              institutionsTableEnd
            )}
            owners={owners}
            onAddAdmin={(institutionId: string) => addAdmin(institutionId)}
          />
          <InstitutionsTablePaginationContainer>
            <SelectFlexContainer>
              <Select
                className="select"
                value={String(institutionsPerPage)}
                options={[
                  {
                    value: '5',
                    label: '5',
                  },
                  {
                    value: '10',
                    label: '10',
                  },
                  {
                    value: '20',
                    label: '20',
                  },
                ]}
                onValueChange={(value) => {
                  setInsitutionsPerPage(Number(value));
                  setInstitutionsTablePage(1);
                }}
              />
              <SelectTotalPagesContainer>
                Total: {filteredInstitutions.length}
              </SelectTotalPagesContainer>
            </SelectFlexContainer>
            <Paginate
              pageSize={1}
              page={institutionsTablePage}
              totalPages={Math.round(
                filteredInstitutions.length / institutionsPerPage
              )}
              handlePageChange={(newPage) => setInstitutionsTablePage(newPage)}
            />
          </InstitutionsTablePaginationContainer>
        </InsitutionsSectionWrapper>
      </div>
    );
  }
);
